import * as Papa from 'papaparse';
import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxMarkdown } from 'bones/components/molecules/Voicebox/shared/VoiceboxMarkdown';
import { VoiceboxResponseMessage } from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { TableBody } from 'bones/design-system/components/data-table/TableBody';
import { TableFooterStyle4 } from 'bones/design-system/components/data-table/TableFooter';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { Link } from 'bones/design-system/components/Link';
import { SystemMessage } from 'bones/design-system/components/notifications/SystemMessage';

/** Amount of rows the server will limit to. Used for guessing if this is a preview or the full results. */
const SERVICE_ROWS_LIMIT = 100;
const ROWS_PER_PAGE = 10;

export interface VoiceboxCsvTableProps {
  message: VoiceboxResponseMessage;
  messageIdx: number;
  parsed: Papa.ParseResult<Record<string, string>>;
  settings: VoiceboxSettings;
  showDownload?: boolean;
}

export const VoiceboxCsvTable: React.VFC<VoiceboxCsvTableProps> = ({
  message,
  messageIdx,
  parsed,
  settings,
  showDownload,
}) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { downloadCsv, loadingDownloadMessageId } = useVoiceboxConversation();

  const fields = React.useMemo(
    () => parsed.meta.fields || /* istanbul ignore next */ [],
    [parsed]
  );
  const rows = React.useMemo(() => parsed.data, [parsed]);

  const onDownload = React.useCallback(async () => {
    if (loadingDownloadMessageId) {
      return;
    }

    await downloadCsv(message, settings);
  }, [downloadCsv, loadingDownloadMessageId, message, settings]);

  const [page, setPage] = React.useState(1);
  const pages = Math.ceil(rows.length / ROWS_PER_PAGE);

  const link = (
    <Link
      disabled={Boolean(loadingDownloadMessageId)}
      onClick={onDownload}
      rightIcon={
        loadingDownloadMessageId === message.messageId ? LoadingIcon : undefined
      }
      role="button"
      size="md"
      testId={testIds.messages.responseDownloadCsv(
        message.messageId,
        messageIdx
      )}
    >
      {copy.csvTable.link}
    </Link>
  );

  let footer: JSX.Element | null;
  if (rows.length === SERVICE_ROWS_LIMIT) {
    footer = (
      <SystemMessage
        text={
          <>
            {copy.csvTable.beforeLink}
            &nbsp;
            {showDownload ? link : copy.csvTable.link}
            &nbsp;
            {copy.csvTable.afterLink}
          </>
        }
        variant="info"
      />
    );
  } else {
    footer = showDownload ? link : null;
  }

  return (
    <div
      className="vb-voicebox-csv-table"
      data-testid={testIds.csvTable.container}
    >
      <div className="vb-data-table">
        <TableBody
          body={rows
            .slice(ROWS_PER_PAGE * (page - 1), ROWS_PER_PAGE * page)
            .map((row, rowIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={rowIdx}>
                {fields.map((field, colIdx) => (
                  <td key={field}>
                    <VoiceboxMarkdown
                      linkTestId={(iri) =>
                        testIds.csvTable.link(iri, rowIdx, colIdx)
                      }
                      markdown={row[field]}
                      messageId={message.messageId}
                      settings={settings}
                    />
                  </td>
                ))}
              </React.Fragment>
            ))}
          header={
            <>
              {fields.map((field) => (
                <td key={field}>{field}</td>
              ))}
            </>
          }
          size="md"
        />
        {pages > 1 ? (
          <TableFooterStyle4
            onPage={setPage}
            page={page}
            pages={pages}
            size="md"
          />
        ) : null}
      </div>

      {footer}
    </div>
  );
};
